import React, { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

export interface ExecutionModuleRibbonState {
  create?: {
    enabled: boolean;
    onClick?: () => void;
    tooltip?: string;
  };
  instance?: {
    pauseLabel?: string;
    onPauseOrResume?: () => void;
    canPauseOrResume?: boolean;
    onCancel?: () => void;
    canCancel?: boolean;
    onReprocess?: () => void;
    canReprocess?: boolean;
    busy?: boolean;
  };
  stage?: {
    onStart?: () => void;
    canStart?: boolean;
    onComplete?: () => void;
    canComplete?: boolean;
    onBlock?: () => void;
    canBlock?: boolean;
    onOpenAction?: () => void;
    canOpenAction?: boolean;
  };
  engineering?: {
    onViewBlueprint?: () => void;
    canViewBlueprint?: boolean;
    onCompareVersion?: () => void;
    canCompareVersion?: boolean;
    onStandardVsReal?: () => void;
    canStandardVsReal?: boolean;
  };
  refresh?: {
    onRefresh?: () => void;
    isRefreshing?: boolean;
  };
  export?: {
    onExport?: () => void;
    canExport?: boolean;
  };
}

// ── Setters context (used by pages that SET state) ────────────────────────────
// Contains only stable setter functions — NEVER changes reference.
// Consumers (Dashboard, InstancePage) will NEVER re-render due to context changes.
interface ExecutionModuleWriterContextValue {
  setRibbonState: (state: ExecutionModuleRibbonState | null) => void;
  clearRibbonState: () => void;
  setFiltersSlot: (slot: React.ReactNode | null) => void;
}

// ── Ribbon state reader context (used by the hub/ribbon that READS ribbon state) ──
interface ExecutionRibbonStateContextValue {
  ribbonState: ExecutionModuleRibbonState | null;
}

// ── Filters slot reader context (used by the hub/ribbon that READS the slot) ──
interface ExecutionFiltersSlotContextValue {
  filtersSlot: React.ReactNode | null;
}

const ExecutionModuleContext = createContext<ExecutionModuleWriterContextValue>({
  setRibbonState: () => {},
  clearRibbonState: () => {},
  setFiltersSlot: () => {},
});

const ExecutionRibbonStateContext = createContext<ExecutionRibbonStateContextValue>({
  ribbonState: null,
});

const ExecutionFiltersSlotContext = createContext<ExecutionFiltersSlotContextValue>({
  filtersSlot: null,
});

// Only compares non-function (data) properties. Function references change when
// callbacks are recreated on render but the underlying data is identical — those
// changes must NOT trigger a ribbon state update or they cause a render loop.
function areRibbonSectionsEqual<T extends Record<string, unknown> | undefined>(left?: T, right?: T) {
  if (left === right) return true;
  if (!left || !right) return left === right;
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => {
    // Skip function-valued properties — only data values drive visual differences
    if (typeof left[key] === 'function' || typeof right[key] === 'function') return true;
    return left[key] === right[key];
  });
}

function areRibbonStatesEqual(
  left: ExecutionModuleRibbonState | null,
  right: ExecutionModuleRibbonState | null,
) {
  if (left === right) return true;
  if (!left || !right) return left === right;
  return (
    areRibbonSectionsEqual(left.create, right.create) &&
    areRibbonSectionsEqual(left.instance, right.instance) &&
    areRibbonSectionsEqual(left.stage, right.stage) &&
    areRibbonSectionsEqual(left.engineering, right.engineering) &&
    areRibbonSectionsEqual(left.refresh, right.refresh) &&
    areRibbonSectionsEqual(left.export, right.export)
  );
}

export function ExecutionModuleProvider({ children }: { children: ReactNode }) {
  const [ribbonState, setRibbonStateInternal] = useState<ExecutionModuleRibbonState | null>(null);
  // filtersSlot lives in its own state; only the hub (reader) re-renders when it changes
  const [filtersSlot, setFiltersSlotInternal] = useState<React.ReactNode | null>(null);
  // Ref guards bypass useState dispatch entirely when data values haven't changed
  const ribbonStateRef = useRef<ExecutionModuleRibbonState | null>(null);
  const filtersSlotRef = useRef<React.ReactNode | null>(null);

  const setRibbonState = useCallback((nextState: ExecutionModuleRibbonState | null) => {
    if (areRibbonStatesEqual(ribbonStateRef.current, nextState)) return;
    // Store the full nextState (including new function refs) so hub always calls fresh handlers
    ribbonStateRef.current = nextState;
    setRibbonStateInternal(nextState);
  }, []);

  const clearRibbonState = useCallback(() => {
    if (ribbonStateRef.current === null) return;
    ribbonStateRef.current = null;
    setRibbonStateInternal(null);
  }, []);

  // Ref-guard: only update state when the slot reference actually changes
  const setFiltersSlot = useCallback((slot: React.ReactNode | null) => {
    if (filtersSlotRef.current !== slot) {
      filtersSlotRef.current = slot;
      setFiltersSlotInternal(slot);
    }
  }, []);

  // Setters-only value: contains ONLY stable callbacks — reference NEVER changes.
  // Consumers (Dashboard, InstancePage) never re-render from ribbon/slot state changes.
  const writerValue = useMemo<ExecutionModuleWriterContextValue>(
    () => ({ setRibbonState, clearRibbonState, setFiltersSlot }),
    [setRibbonState, clearRibbonState, setFiltersSlot],
  );

  // Ribbon reader value: only the ribbon state — hub/ribbon reads this
  const ribbonReaderValue = useMemo<ExecutionRibbonStateContextValue>(
    () => ({ ribbonState }),
    [ribbonState],
  );

  // Filters slot reader value: only the slot — hub/ribbon reads this
  const slotReaderValue = useMemo<ExecutionFiltersSlotContextValue>(
    () => ({ filtersSlot }),
    [filtersSlot],
  );

  return (
    <ExecutionModuleContext.Provider value={writerValue}>
      <ExecutionRibbonStateContext.Provider value={ribbonReaderValue}>
        <ExecutionFiltersSlotContext.Provider value={slotReaderValue}>
          {children}
        </ExecutionFiltersSlotContext.Provider>
      </ExecutionRibbonStateContext.Provider>
    </ExecutionModuleContext.Provider>
  );
}

/** Used by pages that SET ribbon state / filter slot (dashboard, gestao, etc.) */
export function useExecutionModuleContext() {
  return useContext(ExecutionModuleContext);
}

/** Used by the hub/ribbon that READS ribbon state */
export function useExecutionRibbonState() {
  return useContext(ExecutionRibbonStateContext).ribbonState;
}

/** Used by the hub/ribbon that READS the filter slot */
export function useExecutionFiltersSlot() {
  return useContext(ExecutionFiltersSlotContext).filtersSlot;
}
